import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Alert,
  Autocomplete,
  TextField,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  ListSubheader,
} from "@mui/material";
import {
  AttachFile,
  CloudUpload,
  Close,
  Download,
  Visibility,
} from "@mui/icons-material";
import {
  LabeledTextField,
  LabeledSelect,
  LabeledField,
} from "@/__core/components/LabeledField";
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import { UserOptionDisplay } from '@/__core/components';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { NumericFormat } from 'react-number-format';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { fetchWalletsThunk } from '../../settings/wallet/redux/WalletRedux';
import { fetchSuppliers } from '../../suppliers/redux/SupplierRedux';
import { fetchTransactionTypesThunk } from '../../settings/transaction_type/redux/TransactionTypeRedux';
import UniversalFileViewer from '../../documents/components/UniversalFileViewer';
import { usePermissions } from '@/__core/permissions/hooks/usePermissions';
import { useFinancePermissions } from '@/__core/permissions/hooks/usePermissionChecker';
import { RootState } from '@/setup';
import {
  hasPermission as hasPermissionFromStorage,
  getCurrentPermissions,
} from '@/modules/auth/helpers/CompanyStorage';

const parseNumericInput = (rawValue: string, thousandSep: string, decimalSep: string): number => {
  if (!rawValue) return 0;

  const decimalIndex = decimalSep ? rawValue.lastIndexOf(decimalSep) : -1;
  let integerPart = decimalIndex !== -1 ? rawValue.substring(0, decimalIndex) : rawValue;
  let decimalPart = decimalIndex !== -1 ? rawValue.substring(decimalIndex + 1) : '';

  if (thousandSep) {
    const escaped = thousandSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    integerPart = integerPart.replace(new RegExp(escaped, 'g'), '');
  }

  const cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

interface IncomeExpenseData {
  posting_date: string;
  document_date: string;
  transaction_type: string;
  actual_amount: number;
  total_amount: number;
  wallet: string;
  note: string;
  order: string;
  customer: string;
  employee: string;
  supplier: string;
  company_id?: string;
  company?: string;
  debt_type?: string;
  status: string;
}

interface NewIncomeExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (incomeExpenseData: IncomeExpenseData) => Promise<void>;
  editingIncomeExpense?: any | null;
  isEditMode?: boolean;
  orderId?: string;
  orderData?: any;
  contextCompanyId?: string;
  transactionCategory?: 'income' | 'expense';
  defaultWalletId?: string;
}

type TransactionStatusOption = {
  id: string;
  name: string;
  code?: string;
  is_default?: boolean;
  color?: string;
  sort_order?: number;
  group?: {
    id?: string;
    name?: string;
    sort_order?: number;
  };
};

const NewIncomeExpenseModal: React.FC<NewIncomeExpenseModalProps> = ({
  open,
  onClose,
  onSubmit,
  editingIncomeExpense = null,
  isEditMode = false,
  orderId,
  orderData,
  contextCompanyId,
  transactionCategory,
  defaultWalletId,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize permissions
  usePermissions(); // Ensure permissions context is initialized
  
  // Get permissions and selected company
  const { hasPermission } = usePermissions();
  const { canRead, canCreate, canUpdate } = useFinancePermissions();
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  
  const canApproveFromStorage = React.useMemo(() => {
    if (hasPermissionFromStorage('transaction', 'approve_transaction')) {
      return true;
    }

    const permissions = getCurrentPermissions();
    const transactionActions = [
      ...(permissions.transaction || []),
      ...(permissions.transactions || []),
    ]
      .map((action) => String(action || '').trim().toLowerCase())
      .filter(Boolean);

    return transactionActions.some((action) =>
      ['approve_transaction', 'approve', 'duyet', 'duyệt'].includes(action)
    );
  }, [selectedCompany?.company_id]);

  const canApproveByHook = hasPermission('transaction', 'approve_transaction');
  const canApprove = canApproveByHook || canApproveFromStorage;
  
  // Check income/expense only permissions
  const canViewIncomeOnly = hasPermission('transaction', 'view_income_only');
  const canViewExpenseOnly = hasPermission('transaction', 'view_expense_only');
  
  // Determine if user can edit (create for new, update for existing)
  const canEdit = isEditMode ? canUpdate : canCreate;
  
  // Check if user only has view permission (canRead but no create/update)
  const isViewOnly = canRead && !canCreate && !canUpdate;
  
  // Determine if all fields should be disabled
  // Disable if: user only has view permission OR cannot edit (no create/update permission)
  const isAllFieldsDisabled = isViewOnly || !canEdit;
  
  // Determine if status dropdown should be disabled
  // Disable if: user is NOT global AND does NOT have approve permission OR all fields are disabled
  const isStatusDisabled = isAllFieldsDisabled || !canApprove;

  const resolvedContextCompanyId = useMemo(() => {
    const normalizeCompanyId = (value: any) => {
      const normalized = String(value || '').trim();
      if (!normalized || normalized === 'all' || normalized === 'global') {
        return '';
      }
      return normalized;
    };

    return (
      normalizeCompanyId(orderData?.company_id) ||
      normalizeCompanyId(orderData?.company?.id) ||
      normalizeCompanyId(contextCompanyId) ||
      normalizeCompanyId(selectedCompany?.company_id) ||
      ''
    );
  }, [contextCompanyId, orderData?.company?.id, orderData?.company_id, selectedCompany?.company_id]);

  // Generate unique instance ID for this component - more robust generation
  const instanceId = React.useMemo(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `income-expense-${timestamp}-${random}`;
  }, []);
  const [employees, setEmployees] = useState<any[]>([]);
  const [wallets, setWallets] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    employees: false,
    wallets: false,
    orders: false,
    suppliers: false,
    transactionTypes: false,
    statuses: false,
  });
  const [transactionStatuses, setTransactionStatuses] = useState<TransactionStatusOption[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{ id: string; name: string } | null>(null);
  const [newIncomeExpense, setNewIncomeExpense] = useState<IncomeExpenseData>({
    posting_date: new Date().toISOString().split('T')[0],
    document_date: new Date().toISOString().split('T')[0],
    transaction_type: "",
    actual_amount: 0,
    total_amount: 0,
    wallet: "",
    note: "",
    order: orderId || "",
    customer: "",
    employee: "",
    supplier: "",
    debt_type: undefined,
    status: "pending",
  });

  const wasOpenRef = useRef(false);

  const applyDefaultWallet = useCallback(
    (walletList: any[]) => {
      if (!open || isEditMode) return;
      if (!Array.isArray(walletList) || walletList.length === 0) return;

      const preferredId = defaultWalletId || walletList.find((wallet: any) => Boolean(wallet.is_default))?.id;
      if (!preferredId) return;

      const preferredWalletId = String(preferredId);
      setNewIncomeExpense((prev) => {
        const current = String(prev.wallet || '').trim();
        const currentStillValid =
          current && walletList.some((wallet: any) => String(wallet.id) === current);
        if (currentStillValid) return prev;
        return { ...prev, wallet: preferredWalletId };
      });
    },
    [open, isEditMode, defaultWalletId],
  );

  const normalizeStatusToken = useMemo(() => {
    return (value: string) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
  }, []);

  const isApprovedStatus = useMemo(() => {
    return (status: TransactionStatusOption) => {
      const codeToken = normalizeStatusToken(status.code || '');
      const nameToken = normalizeStatusToken(status.name || '');
      return ['duyet', 'approved', 'approve', 'da_duyet'].includes(codeToken)
        || ['duyet', 'approved', 'approve', 'da_duyet'].includes(nameToken);
    };
  }, [normalizeStatusToken]);

  const resolveDefaultStatusId = useMemo(() => {
    return (statuses: TransactionStatusOption[]) => {
      if (!Array.isArray(statuses) || statuses.length === 0) {
        return 'pending';
      }

      const candidateStatuses = canApprove
        ? statuses
        : statuses.filter((status) => !isApprovedStatus(status));

      const source = candidateStatuses.length > 0 ? candidateStatuses : statuses;

      const apiDefault = source.find((status) => status.is_default);
      if (apiDefault?.id) return apiDefault.id;

      const draftLike = source.find((status) => {
        const codeToken = normalizeStatusToken(status.code || '');
        const nameToken = normalizeStatusToken(status.name || '');
        return ['nhap', 'draft', 'pending'].includes(codeToken)
          || ['nhap', 'draft', 'pending'].includes(nameToken);
      });
      if (draftLike?.id) return draftLike.id;

      return source[0]?.id || 'pending';
    };
  }, [canApprove, isApprovedStatus, normalizeStatusToken]);

  const hasStatusGroup = useMemo(() => {
    const uniqueGroups = new Set(
      transactionStatuses
        .map((status) => status.group?.name?.trim())
        .filter((name): name is string => Boolean(name))
    );

    return uniqueGroups.size > 1;
  }, [transactionStatuses]);

  // Reset form only when modal opens (avoid re-reset when statuses/wallets load later)
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!open) {
      setSelectedFiles([]);
      return;
    }

    if (!justOpened) {
      return;
    }

    if (isEditMode && editingIncomeExpense) {
        // Normalize actual_amount first
        const actualAmount = editingIncomeExpense.actual_amount !== undefined && editingIncomeExpense.actual_amount !== null
          ? Number(editingIncomeExpense.actual_amount)
          : ((editingIncomeExpense as any).actualAmount !== undefined && (editingIncomeExpense as any).actualAmount !== null
            ? Number((editingIncomeExpense as any).actualAmount)
            : 0);
        
        // Normalize total_amount - CRITICAL: 0 is a valid value, must check explicitly
        // Only fallback to actual_amount if total_amount is truly null or undefined
        let totalAmount = 0;
        
        // Get total_amount value (check both snake_case and camelCase)
        let totalAmountValue: any = undefined;
        if (editingIncomeExpense.total_amount !== undefined) {
          // total_amount exists (could be 0, null, or a number)
          totalAmountValue = editingIncomeExpense.total_amount;
        } else if ((editingIncomeExpense as any).totalAmount !== undefined) {
          // totalAmount (camelCase) exists
          totalAmountValue = (editingIncomeExpense as any).totalAmount;
        }
        
        // Check if total_amount has a valid value
        // IMPORTANT: We must check if the value is explicitly null/undefined, not just falsy
        // Because 0 is falsy but is a valid value for total_amount
        if (totalAmountValue !== undefined && totalAmountValue !== null) {
          // total_amount has a value (including 0) - use it
          const numValue = Number(totalAmountValue);
          totalAmount = isNaN(numValue) ? 0 : numValue;
        } else {
          // total_amount is null or undefined - use actual_amount as fallback
          totalAmount = actualAmount;
        }
        
        // Normalize transaction data - handle both snake_case and camelCase
        const normalizedData = {
          ...editingIncomeExpense,
          actual_amount: actualAmount,
          total_amount: totalAmount,
        };
        
        // Pre-fill form with editing income expense data
        setNewIncomeExpense({
          posting_date: normalizedData.posting_date || new Date().toISOString().split('T')[0],
          document_date: normalizedData.document_date || new Date().toISOString().split('T')[0],
          transaction_type: normalizedData.transaction_type || "",
          actual_amount: normalizedData.actual_amount,
          total_amount: normalizedData.total_amount,
          wallet: normalizedData.wallet || "",
          note: normalizedData.note || "",
          order: normalizedData.order || "",
          customer: normalizedData.customer || "",
          employee: normalizedData.employee || "",
          supplier: normalizedData.supplier || "",
          debt_type: normalizedData.debt_type || undefined,
          status: normalizedData.status || "pending",
        });
    } else {
      // Reset form for new income expense
      setNewIncomeExpense({
        posting_date: new Date().toISOString().split('T')[0],
        document_date: new Date().toISOString().split('T')[0],
        transaction_type: "",
        actual_amount: 0,
        total_amount: 0,
        wallet: "",
        note: "",
        order: orderId || "",
        customer: "",
        employee: "",
        supplier: "",
        debt_type: undefined,
        status: transactionStatuses.length > 0 ? resolveDefaultStatusId(transactionStatuses) : "pending",
      });
    }
    setError(null);
  }, [open, isEditMode, editingIncomeExpense, orderId, resolveDefaultStatusId, transactionStatuses]);
  
  // Align the current value with the resolved default when the user cannot approve or the current status is invalid.
  useEffect(() => {
    if (transactionStatuses.length === 0) return;

    const resolvedDefaultStatusId = resolveDefaultStatusId(transactionStatuses);
    const hasCurrentStatus = transactionStatuses.some((status) => status.id === newIncomeExpense.status);

    if ((isStatusDisabled && newIncomeExpense.status !== resolvedDefaultStatusId) || !hasCurrentStatus) {
      setNewIncomeExpense(prev => ({
        ...prev,
        status: resolvedDefaultStatusId,
      }));
    }
  }, [isStatusDisabled, newIncomeExpense.status, resolveDefaultStatusId, transactionStatuses]);

  // Load uploaded documents if in edit mode
  useEffect(() => {
    const loadDocuments = async () => {
      if (!isEditMode || !editingIncomeExpense?.id) return;

      try {
        setLoadingDocuments(true);

        const { getContentTypeId, getDocumentsByObject } = await import('@/modules/documents/redux/DocumentCRUD');
        const contentTypeId = await getContentTypeId('transaction');
        const documents = await getDocumentsByObject(contentTypeId, editingIncomeExpense.id);

        setUploadedDocuments(documents);
      } catch (error) {
        console.error(`[${instanceId}] Error loading documents:`, error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [isEditMode, editingIncomeExpense?.id, instanceId]);

  // Load employees, wallets and orders data
  useEffect(() => {
    const loadData = async () => {
      if (!open) return;

      try {
        setLoading(prev => ({ ...prev, employees: true }));
        // Use the existing axios instance with authentication
        const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
        const response = await axiosInstance.get('/employees/', { params: { no_pagination: true } });
        const employees = response.data.results || response.data || [];
        setEmployees(employees);
        setLoading(prev => ({ ...prev, employees: false }));
      } catch (error) {
        setLoading(prev => ({ ...prev, employees: false }));
      }

      try {
        setLoading(prev => ({ ...prev, transactionTypes: true }));
        const transactionTypesResult = await dispatch(fetchTransactionTypesThunk());
        if (fetchTransactionTypesThunk.fulfilled.match(transactionTypesResult)) {
          setTransactionTypes(transactionTypesResult.payload || []);
        }
        setLoading(prev => ({ ...prev, transactionTypes: false }));
      } catch (error) {
        setLoading(prev => ({ ...prev, transactionTypes: false }));
      }

      try {
        setLoading(prev => ({ ...prev, wallets: true }));
        const walletFetchParams = resolvedContextCompanyId
          ? {
              company: resolvedContextCompanyId,
              company_id: resolvedContextCompanyId,
              no_pagination: true,
            }
          : { no_pagination: true };

        const walletsResult = await dispatch(fetchWalletsThunk(walletFetchParams));
        if (fetchWalletsThunk.fulfilled.match(walletsResult)) {
          const walletItems = walletsResult.payload || [];
          const scopedWallets = resolvedContextCompanyId
            ? walletItems.filter((wallet: any) => {
                const walletCompanyId = String(
                  wallet?.company?.id || wallet?.company || wallet?.company_id || '',
                ).trim();
                return walletCompanyId === resolvedContextCompanyId;
              })
            : walletItems;

          const normalizedWallets = scopedWallets.map((wallet: any) => ({
            ...wallet,
            id: String(wallet.id),
            is_default: Boolean(wallet.is_default),
          }));

          setWallets(normalizedWallets);
          applyDefaultWallet(normalizedWallets);
        }
        setLoading(prev => ({ ...prev, wallets: false }));
      } catch (error) {
        setLoading(prev => ({ ...prev, wallets: false }));
      }

      try {
        setLoading(prev => ({ ...prev, suppliers: true }));
        const suppliersResult = await dispatch(fetchSuppliers({ no_pagination: true }));
        if (fetchSuppliers.fulfilled.match(suppliersResult)) {
          // Handle both array response and paginated response
          const payload = suppliersResult.payload;
          const suppliersData = Array.isArray(payload) ? payload : (payload?.results || []);
          setSuppliers(suppliersData);
        } else {
          console.warn(`[${instanceId}] Suppliers loading failed:`, suppliersResult);
        }
        setLoading(prev => ({ ...prev, suppliers: false }));
      } catch (error) {
        setLoading(prev => ({ ...prev, suppliers: false }));
      }

      try {
        setLoading(prev => ({ ...prev, statuses: true }));
        const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');

        const fetchStatusesByModule = async () => {
          const moduleCandidates = ['finance', 'transaction'];
          for (const moduleName of moduleCandidates) {
            try {
              const response = await axiosInstance.get(`/settings/statuses/by-module/?module=${moduleName}`);
              const data = response.data;
              const modulePayload = data?.[moduleName] || data?.finance || data?.transactions || data;

              const normalized: TransactionStatusOption[] = Array.isArray(modulePayload?.groups)
                ? modulePayload.groups.flatMap((group: any) => {
                    const groupStatus = group?.group_status || null;
                    const items = Array.isArray(group?.items) ? group.items : [];
                    return items.map((item: any) => ({
                      id: String(item.id),
                      name: item.name || '',
                      code: item.code || undefined,
                      is_default: Boolean(item.is_default),
                      color: item.color || undefined,
                      sort_order: item.sort_order ?? 0,
                      group: groupStatus
                        ? {
                            id: groupStatus.id,
                            name: groupStatus.name,
                            sort_order: groupStatus.sort_order ?? 999,
                          }
                        : undefined,
                    }));
                  })
                : Array.isArray(modulePayload?.items)
                  ? modulePayload.items.map((item: any) => ({
                      id: String(item.id),
                      name: item.name || '',
                      code: item.code || undefined,
                      is_default: Boolean(item.is_default),
                      color: item.color || undefined,
                      sort_order: item.sort_order ?? 0,
                      group: item?.group_status
                        ? {
                            id: item.group_status.id,
                            name: item.group_status.name,
                            sort_order: item.group_status.sort_order ?? 999,
                          }
                        : undefined,
                    }))
                  : Array.isArray(modulePayload)
                    ? modulePayload.map((item: any) => ({
                        id: String(item.id),
                        name: item.name || '',
                        code: item.code || undefined,
                        is_default: Boolean(item.is_default),
                        color: item.color || undefined,
                        sort_order: item.sort_order ?? 0,
                      }))
                    : [];

              if (normalized.length > 0) {
                return normalized;
              }
            } catch (statusError) {
              // Try next module candidate.
            }
          }

          return [] as TransactionStatusOption[];
        };

        const apiStatuses = await fetchStatusesByModule();
        const fallbackStatuses: TransactionStatusOption[] = canApprove
          ? [
              { id: 'pending', name: 'Nháp', code: 'NHAP', is_default: true, color: '', sort_order: 1 },
              { id: 'approved', name: 'Duyệt', code: 'DUYET', is_default: false, color: '#10b981', sort_order: 2 },
            ]
          : [{ id: 'pending', name: 'Nháp', code: 'NHAP', is_default: true, color: '', sort_order: 1 }];

        const sourceStatuses = apiStatuses.length > 0 ? apiStatuses : fallbackStatuses;
        const filteredByPermission = (() => {
          if (canApprove) return sourceStatuses;
          const nonApproved = sourceStatuses.filter((status) => !isApprovedStatus(status));
          return nonApproved.length > 0 ? nonApproved : sourceStatuses;
        })();

        const normalizeStatusName = (value: string) => (value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, ' ');

        const getStatusQualityScore = (status: TransactionStatusOption) => {
          const name = status.name || '';
          let score = 0;

          if (/[^\u0000-\u007f]/.test(name)) {
            score += 4;
          }
          if (status.id === 'pending' || status.id === 'approved') {
            score += 3;
          }
          if (status.color) {
            score += 1;
          }

          return score;
        };

        const uniqueStatusesMap = new Map<string, TransactionStatusOption>();
        filteredByPermission.forEach((status) => {
          const key = normalizeStatusName(status.name || status.id);
          const existing = uniqueStatusesMap.get(key);
          if (!existing || getStatusQualityScore(status) > getStatusQualityScore(existing)) {
            uniqueStatusesMap.set(key, status);
          }
        });

        const sortedStatuses = Array.from(uniqueStatusesMap.values()).sort((a, b) => {
          const groupSortA = a.group?.sort_order ?? 999;
          const groupSortB = b.group?.sort_order ?? 999;
          if (groupSortA !== groupSortB) return groupSortA - groupSortB;

          const itemSortA = a.sort_order ?? 0;
          const itemSortB = b.sort_order ?? 0;
          if (itemSortA !== itemSortB) return itemSortA - itemSortB;

          return (a.name || '').localeCompare(b.name || '', 'vi');
        });

        setTransactionStatuses(sortedStatuses);
        setNewIncomeExpense((prev) => ({
          ...prev,
          status: !prev.status || !sortedStatuses.some((status) => status.id === prev.status)
            ? resolveDefaultStatusId(sortedStatuses)
            : prev.status,
        }));
        setLoading(prev => ({ ...prev, statuses: false }));
      } catch (statusError) {
        const fallbackStatuses: TransactionStatusOption[] = canApprove
          ? [
              { id: 'pending', name: 'Nháp', code: 'NHAP', is_default: true, color: '', sort_order: 1 },
              { id: 'approved', name: 'Duyệt', code: 'DUYET', is_default: false, color: '#10b981', sort_order: 2 },
            ]
          : [{ id: 'pending', name: 'Nháp', code: 'NHAP', is_default: true, color: '', sort_order: 1 }];
        setTransactionStatuses(fallbackStatuses);
        setNewIncomeExpense((prev) => ({
          ...prev,
          status: !prev.status || !fallbackStatuses.some((status) => status.id === prev.status)
            ? resolveDefaultStatusId(fallbackStatuses)
            : prev.status,
        }));
        setLoading(prev => ({ ...prev, statuses: false }));
      }

      // Gọi API order để lấy tài khoản thu tiền
      const orderIdToLoad = orderId || (isEditMode && editingIncomeExpense?.order);
      if (orderIdToLoad) {
        try {
          const { default: axiosInstance2 } = await import('@/setup/axios/axiosInstance');
          const response = await axiosInstance2.get(`/orders/${orderIdToLoad}/`);
          const orderData = response.data;
          setOrders([orderData]);
          if (orderData.wallet?.id) {
            setNewIncomeExpense((prev) => ({ ...prev, wallet: String(orderData.wallet.id) }));
          }
        } catch (_err) {
          // bỏ qua lỗi
        }
      }
    };

    loadData();
  }, [
    open,
    dispatch,
    orderId,
    instanceId,
    isEditMode,
    editingIncomeExpense?.order,
    canApprove,
    isApprovedStatus,
    resolveDefaultStatusId,
    resolvedContextCompanyId,
    applyDefaultWallet,
  ]);

  const handleInputChange = (field: keyof IncomeExpenseData, value: any) => {
    setNewIncomeExpense(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation - Allow negative values and zero (<= 0)
    // Only check if values are provided, not if they are positive
    if (!newIncomeExpense.transaction_type) {
      setError("Mục thu chi là bắt buộc");
      return;
    }

    if (newIncomeExpense.actual_amount === undefined || newIncomeExpense.actual_amount === null) {
      setError("Thực thu/chi là bắt buộc");
      return;
    }

    if (newIncomeExpense.total_amount === undefined || newIncomeExpense.total_amount === null) {
      setError("Tổng giá trị là bắt buộc");
      return;
    }

    setIsSubmitting(true);
    try {
      // Include selected files in the submit data
      const submitData = {
        ...newIncomeExpense,
        company_id: resolvedContextCompanyId || undefined,
        company: resolvedContextCompanyId || undefined,
        selectedFiles: selectedFiles
      };
      await onSubmit(submitData);
      onClose();
    } catch (err: any) {
      setError(err.message || "Không thể tạo thu chi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => {
        const newFiles = [...prev, ...files];
        return newFiles;
      });
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      return newFiles;
    });
  };

  const handleRemoveUploadedDocument = async (documentId: string) => {
    try {
      const { deleteDocument } = await import('@/modules/documents/redux/DocumentCRUD');
      await deleteDocument(documentId);

      // Update local state
      setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
    } catch (error) {
      setError('Không thể xóa file');
    }
  };

  const handleDownloadDocument = async (documentId: string, fileName: string) => {
    try {
      // Get download URL from backend
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get(`documents/${documentId}/download/`, {
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      setError('Không thể tải xuống file');
    }
  };

  const handlePreviewDocument = (documentId: string, fileName: string) => {
    setPreviewDocument({ id: documentId, name: fileName });
    setPreviewModalOpen(true);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewDocument(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const parentTransactionTypeIds = useMemo(() => {
    const ids = new Set<string>();
    if (Array.isArray(transactionTypes)) {
      transactionTypes.forEach((type: any) => {
        if (type?.parent_id) {
          ids.add(String(type.parent_id));
        }
      });
    }
    return ids;
  }, [transactionTypes]);

  const transactionTypeOptions = useMemo(() => {
    const leafTransactionTypes = Array.isArray(transactionTypes)
      ? transactionTypes.filter((type: any) => !parentTransactionTypeIds.has(String(type.id)))
      : [];

    const filteredTransactionTypes = leafTransactionTypes.filter((type: any) => {
      if (transactionCategory) {
        return type.category === transactionCategory;
      }
      if (canViewIncomeOnly && !canViewExpenseOnly) {
        return type.category === 'income';
      }
      if (canViewExpenseOnly && !canViewIncomeOnly) {
        return type.category !== 'income';
      }
      return true;
    });

    return filteredTransactionTypes
      .sort((a: any, b: any) => {
        const categoryOrderA = a.category === 'income' ? 0 : 1;
        const categoryOrderB = b.category === 'income' ? 0 : 1;
        if (categoryOrderA !== categoryOrderB) {
          return categoryOrderA - categoryOrderB;
        }
        return (a.order_number || 0) - (b.order_number || 0);
      })
      .map((type: any) => ({
        ...type,
        // Prefer explicit color returned by API, fallback to category-based colors
        categoryColor: (typeof type.color === 'string' && type.color) ? type.color : (type.category === 'income' ? '#22c55e' : '#ef4444'),
      }));
  }, [transactionTypes, parentTransactionTypeIds, canViewIncomeOnly, canViewExpenseOnly, transactionCategory]);

  // Convert employees to options format
  const employeeOptions = Array.isArray(employees) ? employees.map((employee: any) => ({
    id: employee.id,
    name: employee.name,
    nickname: employee.nickname || '',
    avatar: employee.user?.avatar || null,
    image1: employee.image1 || null,
    label: employee.nickname || employee.name,
  })) : [];

  // Convert wallets to options format
  const walletOptions = Array.isArray(wallets) ? wallets.map((wallet: any) => ({
    id: wallet.id,
    name: wallet.name,
  })) : [];

  // Fallback: apply default wallet when wallets state updates after modal open
  useEffect(() => {
    applyDefaultWallet(wallets);
  }, [applyDefaultWallet, wallets]);

  // Convert orders to options format
  const orderOptions = Array.isArray(orders) ? orders.map((order: any) => ({
    id: order.id,
    name: order.code,
  })) : [];

  // Convert suppliers to options format
  const supplierOptions = Array.isArray(suppliers) ? suppliers.map((supplier: any) => ({
    id: supplier.id,
    name: supplier.name,
    code: supplier.code,
    label: supplier.name, // Only show name, not code
  })) : [];

  const isSubmitDisabled =
    isAllFieldsDisabled ||
    isSubmitting ||
    newIncomeExpense.actual_amount === undefined ||
    newIncomeExpense.actual_amount === null ||
    newIncomeExpense.total_amount === undefined ||
    newIncomeExpense.total_amount === null;

  const sections = useMemo<AppFormSection[]>(() => [
    {
      key: 'income-expense-form',
      fields: [
        {
          key: 'form-content',
          label: '',
          type: 'custom',
          standalone: true,
          gridSize: { xs: 12 },
          render: () => (
            <>
              <Grid container rowSpacing={0} columnSpacing={4}>
                {/* Left Column */}
                <Grid size={{ xs: 12, md: 6 }}>
                  {/* Mã đơn hàng */}
                  <LabeledSelect
                    className="Select__inline-icon"
                    label="Mã đơn hàng"
                    value={orderOptions.find(opt => opt.id === newIncomeExpense.order) ? newIncomeExpense.order : ""}
                    onChange={(e) => handleInputChange('order', e.target.value)}
                    options={orderOptions}
                    placeholder="Chọn đơn hàng"
                    disabled
                    fullWidth
                  />

                  {/* Ngày hạch toán */}
                  <LabeledField label="Ngày hạch toán" required>
                    <DatePicker
                      value={newIncomeExpense.posting_date ? new Date(newIncomeExpense.posting_date) : null}
                      onChange={(newValue) => {
                        if (newValue && !isAllFieldsDisabled) {
                          const formattedDate = newValue.toISOString().split('T')[0];
                          handleInputChange('posting_date', formattedDate);
                        }
                      }}
                      format="dd/MM/yyyy"
                      disabled={isAllFieldsDisabled}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          variant: 'standard',
                          disabled: isAllFieldsDisabled,
                        }
                      }}
                    />
                  </LabeledField>

                  {/* Ngày chứng từ */}
                  <LabeledField label="Ngày chứng từ" required>
                    <DatePicker
                      value={newIncomeExpense.document_date ? new Date(newIncomeExpense.document_date) : null}
                      onChange={(newValue) => {
                        if (newValue && !isAllFieldsDisabled) {
                          const formattedDate = newValue.toISOString().split('T')[0];
                          handleInputChange('document_date', formattedDate);
                        }
                      }}
                      format="dd/MM/yyyy"
                      disabled={isAllFieldsDisabled}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          variant: 'standard',
                          disabled: isAllFieldsDisabled,
                        }
                      }}
                    />
                  </LabeledField>

                  {/* Mục thu chi */}
                  <LabeledField label="Mục thu chi" required>
                    <Autocomplete
                      fullWidth
                      sx={sharedFormStyles.autocompleteFullWidth}
                      options={transactionTypeOptions}
                      value={transactionTypeOptions.find((type: any) => type.id === newIncomeExpense.transaction_type) || null}
                      onChange={(event, newValue) => {
                        if (!isAllFieldsDisabled) {
                          handleInputChange('transaction_type', newValue?.id || '');
                        }
                      }}
                      getOptionLabel={(option: any) => option.name || ''}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                      loading={loading.transactionTypes}
                      disabled={isAllFieldsDisabled}
                      renderInput={(params) => {
                        const selectedType = transactionTypeOptions.find((type: any) => type.id === newIncomeExpense.transaction_type);
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            placeholder="Chọn mục thu chi"
                            size="small"
                            variant="standard"
                            required
                            sx={{
                              '& .MuiInputBase-input': {
                                color: selectedType?.categoryColor || 'inherit',
                                fontWeight: selectedType?.categoryColor ? 500 : 'inherit',
                              },
                            }}
                          />
                        );
                      }}
                      renderOption={(props, option: any) => (
                        <Box component="li" {...props}>
                          <Typography
                            variant="body2"
                            sx={{
                              ...sharedFormStyles.optionNameText,
                              color: option.categoryColor,
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                      )}
                    />
                  </LabeledField>

                  {/* Tài khoản/Ví */}
                  <LabeledSelect
                    className="Select__inline-icon"
                    label="Tài khoản/Ví"
                    value={newIncomeExpense.wallet || ""}
                    onChange={(e) => {
                      if (!isAllFieldsDisabled) {
                        handleInputChange('wallet', e.target.value);
                      }
                    }}
                    options={walletOptions}
                    placeholder="Chọn tài khoản/ví"
                    fullWidth
                    disabled={isAllFieldsDisabled}
                  />
                </Grid>

                {/* Right Column */}
                <Grid size={{ xs: 12, md: 6 }}>
                  {/* Thực thu/chi */}
                  <LabeledField label="Thực thu/chi">
                  <NumericFormat
                    value={newIncomeExpense.actual_amount || 0}
                    onChange={(event) => {
                      if (!isAllFieldsDisabled) {
                        const rawValue = event.target.value;
                        const amount = parseNumericInput(rawValue, '.', ',');
                        handleInputChange('actual_amount', amount);
                        // Chỉ tự động cập nhật Tổng giá trị bằng Thực thu/chi khi tạo mới
                        // Ở chế độ edit, Tổng giá trị và Thực thu/chi độc lập
                        if (!isEditMode) {
                          handleInputChange('total_amount', amount);
                        }
                      }
                    }}
                      decimalScale={0}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative={true}
                      customInput={TextField}
                      size="small"
                      fullWidth
                      variant="standard"
                      disabled={isAllFieldsDisabled}
                      sx={{
                        '& .MuiInputBase-input': {
                          textAlign: 'right',
                          padding: '0 0 4px 0'
                        }
                      }}
                    />
                  </LabeledField>

                  {/* Tổng giá trị */}
                  <LabeledField label="Tổng giá trị">
                  <NumericFormat
                    value={newIncomeExpense.total_amount || 0}
                    onChange={(event) => {
                      if (!isAllFieldsDisabled) {
                        const rawValue = event.target.value;
                        const amount = parseNumericInput(rawValue, '.', ',');
                        handleInputChange('total_amount', amount);
                      }
                    }}
                      decimalScale={0}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative={true}
                      customInput={TextField}
                      size="small"
                      fullWidth
                      variant="standard"
                      disabled={isAllFieldsDisabled}
                      sx={{
                        '& .MuiInputBase-input': {
                          textAlign: 'right',
                          padding: '0 0 4px 0'
                        }
                      }}
                    />
                  </LabeledField>

                  {/* Nhân viên */}
                  <LabeledField label="Nhân viên">
                    <Autocomplete
                      fullWidth
                      sx={sharedFormStyles.autocompleteFullWidth}
                      options={employeeOptions}
                      value={employeeOptions.find(emp => emp.id === newIncomeExpense.employee) || null}
                      onChange={(event, newValue) => {
                        if (!isAllFieldsDisabled) {
                          handleInputChange('employee', newValue?.id || '');
                        }
                      }}
                       getOptionLabel={(option) => option.label || option.name || ''}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      loading={loading.employees}
                      disabled={isAllFieldsDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Chọn nhân viên"
                          size="small"
                          variant="standard"
                          disabled={isAllFieldsDisabled}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <UserOptionDisplay
                            user={{
                              id: option.id,
                              name: option.name,
                              nickname: option.nickname,
                              avatar: option.avatar,
                              image1: option.image1 || null,
                            }}
                          />
                        </Box>
                      )}
                    />
                  </LabeledField>

                  {/* Nhà cung cấp */}
                  <LabeledField label="Nhà cung cấp">
                    <Autocomplete
                      fullWidth
                      sx={sharedFormStyles.autocompleteFullWidth}
                      options={supplierOptions}
                      value={supplierOptions.find(supplier => supplier.id === newIncomeExpense.supplier) || null}
                      onChange={(event, newValue) => {
                        if (!isAllFieldsDisabled) {
                          handleInputChange('supplier', newValue?.id || '');
                        }
                      }}
                      getOptionLabel={(option) => option.label || option.name || ''}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      loading={loading.suppliers}
                      disabled={isAllFieldsDisabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Chọn nhà cung cấp"
                          size="small"
                          variant="standard"
                          disabled={isAllFieldsDisabled}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.name}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  </LabeledField>

                  {/* Trạng thái */}
                  <LabeledField label="Trạng thái" required>
                    <Autocomplete
                      sx={sharedFormStyles.inputArrowAlign}
                      options={transactionStatuses}
                      value={transactionStatuses.find((status) => status.id === newIncomeExpense.status) || null}
                      onChange={(_, newValue) => {
                        if (!isStatusDisabled) {
                          handleInputChange('status', newValue?.id || '');
                        }
                      }}
                      getOptionLabel={(option: any) => option?.name || ''}
                      isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                      groupBy={hasStatusGroup ? ((option: any) => option.group?.name || 'Khác') : undefined}
                      renderGroup={hasStatusGroup ? ((params) => (
                        <li key={params.key} style={{ padding: 0, margin: 0 }}>
                          <ListSubheader
                            disableSticky
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              color: '#4b5563',
                              backgroundColor: '#f9fafb',
                              py: '2px !important',
                              px: '8px',
                              m: '0 !important',
                              minHeight: 'auto !important',
                              lineHeight: '1.2 !important',
                            }}
                          >
                            {params.group}
                          </ListSubheader>
                          {params.children}
                        </li>
                      )) : undefined}
                      loading={loading.statuses}
                      disabled={isStatusDisabled}
                      renderOption={(props, option: any) => (
                        <li
                          {...props}
                          key={option.id}
                          style={{ padding: 0, margin: 0, marginTop: 0, marginBottom: 0 }}
                        >
                          <Typography
                            sx={{
                              color: option.color || 'inherit',
                              paddingLeft: '24px',
                              py: 0.5,
                            }}
                          >
                            {option.name}
                          </Typography>
                        </li>
                      )}
                      renderInput={(params) => {
                        const selectedStatus = transactionStatuses.find((status) => status.id === newIncomeExpense.status);
                        return (
                          <TextField
                            {...params}
                            fullWidth
                            placeholder="Chọn trạng thái"
                            size="small"
                            variant="standard"
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading.statuses ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiInputBase-input': {
                                color: selectedStatus?.color || 'inherit',
                                fontWeight: selectedStatus?.color ? 500 : 'inherit',
                              },
                            }}
                          />
                        );
                      }}
                      noOptionsText="Không tìm thấy trạng thái"
                      clearOnEscape
                      selectOnFocus
                      handleHomeEndKeys
                    />
                  </LabeledField>
                </Grid>
              </Grid>

              {/* Ghi chú */}
              <Box sx={{ mt: 2 }}>
                <LabeledTextField
                  label="Ghi chú"
                  value={newIncomeExpense.note}
                  onChange={(e) => {
                    if (!isAllFieldsDisabled) {
                      handleInputChange('note', e.target.value);
                    }
                  }}
                  placeholder="Nhập ghi chú "
                  disabled={isAllFieldsDisabled}
                />
              </Box>

              {/* File đính kèm */}
              <Box sx={{ mt: 2 }}>
                <LabeledField label="File đính kèm">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* File Upload Area */}
                    <Paper
                      sx={{
                        p: 3,
                        border: '2px dashed',
                        borderColor: selectedFiles.length > 0 ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: isAllFieldsDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        bgcolor: selectedFiles.length > 0 ? 'primary.light' : 'background.paper',
                        opacity: isAllFieldsDisabled ? 0.6 : 1,
                        '&:hover': {
                          borderColor: isAllFieldsDisabled ? 'divider' : 'primary.main',
                          bgcolor: isAllFieldsDisabled ? 'background.paper' : 'primary.light'
                        }
                      }}
                      onClick={() => {
                        if (!isAllFieldsDisabled) {
                          fileInputRef.current?.click();
                        }
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Nhấp để chọn file
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Hoặc kéo thả file vào đây
                      </Typography>
                    </Paper>

                    <input
                      key={`file-input-${instanceId}`}
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      multiple
                      disabled={isAllFieldsDisabled}
                      accept=".txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp"
                    />

                    {/* Uploaded Documents List */}
                    {loadingDocuments && (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Đang tải files...
                        </Typography>
                      </Box>
                    )}

                    {!loadingDocuments && uploadedDocuments.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          Files đã upload ({uploadedDocuments.length})
                        </Typography>
                        {uploadedDocuments.map((doc) => (
                          <Paper
                            key={doc.id}
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: '1px solid',
                              borderColor: 'success.light',
                              borderRadius: 1,
                              bgcolor: 'success.lighter',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachFile sx={{ color: 'success.main' }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {doc.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.file_size ? formatFileSize(doc.file_size) : 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box>
                                <Tooltip title="Xem trước">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePreviewDocument(doc.id, doc.name)}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box>
                                <Tooltip title="Tải xuống">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadDocument(doc.id, doc.name)}
                                    sx={{
                                      color: 'info.main',
                                      display: 'inline-flex !important',
                                      visibility: 'visible !important',
                                      opacity: '1 !important'
                                    }}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box>
                                <Tooltip title="Xóa">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveUploadedDocument(doc.id)}
                                    sx={{
                                      color: 'error.main',
                                      display: 'inline-flex !important',
                                      visibility: 'visible !important',
                                      opacity: '1 !important'
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Selected Files List (New files to upload) */}
                    {selectedFiles.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Files mới chọn ({selectedFiles.length})
                        </Typography>
                        {selectedFiles.map((file, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachFile sx={{ color: 'primary.main' }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              sx={{ color: 'error.main' }}
                            >
                              <Close />
                            </IconButton>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>
                </LabeledField>
              </Box>
            </>
          ),
        },
      ],
    },
  ], [
    employeeOptions,
    instanceId,
    isAllFieldsDisabled,
    isEditMode,
    isStatusDisabled,
    loading.employees,
    loading.suppliers,
    loadingDocuments,
    newIncomeExpense,
    orderOptions,
    selectedFiles,
    transactionTypeOptions,
    supplierOptions,
    transactionStatuses,
    transactionTypes,
    uploadedDocuments,
    walletOptions,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            pb: 2,
          },
        }}
      >
        <DialogTitle>
          {isEditMode ? "Chỉnh sửa thu chi" : "Tạo thu chi mới"}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, px: 2, pb: 2, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <AppForm
            sections={sections}
            onSubmit={handleSubmit}
            onCancel={onClose}
            submitting={isSubmitting}
            submitDisabled={isSubmitDisabled}
            submitLabel={isEditMode ? 'Cập nhật' : 'Tạo thu chi'}
            submittingLabel={isEditMode ? 'Đang cập nhật...' : 'Đang tạo...'}
            cancelLabel='Hủy'
            containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
          />
        </DialogContent>
      </Dialog>

      {/* File Preview Modal */}
      {previewDocument ? (
        <UniversalFileViewer
          open={previewModalOpen}
          onClose={handleClosePreviewModal}
          documentId={previewDocument.id}
          documentName={previewDocument.name}
        />
      ) : null}
    </>
  );
};

export default NewIncomeExpenseModal;
